export type Department = {
  id: number;
  name: string;
  team_id: number;
  // Whether the acting user has joined. Every department on the team is
  // listed, joined or not, because joining is self-serve.
  member: boolean;
};

export async function listDepartments(municipalityId: number): Promise<Department[]> {
  const response = await fetch(`/api/v1/municipalities/${municipalityId}/departments`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error("Failed to load departments");
  }
  return response.json();
}

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? "";
}

function membershipUrl(municipalityId: number, departmentId: number): string {
  return `/api/v1/municipalities/${municipalityId}/departments/${departmentId}/membership`;
}

export async function joinDepartment(municipalityId: number, departmentId: number): Promise<void> {
  const response = await fetch(membershipUrl(municipalityId, departmentId), {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
  });
  if (!response.ok) {
    throw new Error(await errorMessage(response, "Failed to join department"));
  }
}

export async function leaveDepartment(municipalityId: number, departmentId: number): Promise<void> {
  const response = await fetch(membershipUrl(municipalityId, departmentId), {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
  });
  if (!response.ok) {
    // Leaving is refused while the user still holds a role on the department's
    // opportunities; surface that reason rather than a generic failure.
    throw new Error(await errorMessage(response, "Failed to leave department"));
  }
}

async function errorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    const errors = body?.errors;
    if (Array.isArray(errors) && errors.length > 0) return errors.join(", ");
  } catch {
    // Non-JSON body; fall through to the generic message.
  }
  return fallback;
}
